import { useToast } from "@/components/ui/use-toast";
import { writeDepartmentBasedAssignments } from "@/schemas/departmentBasedAssignments";
import { writeRoleBasedAssignments } from "@/schemas/roleBasedAssignments";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useContractorsAssignmentsContext } from "../components/ContractorsAssignmentsContext";
import { WriteContractorAssignee } from "../types";

export const useAddNewEmptyContractorAssignmentMutation = (
  onCloseDialog: () => void,
) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const {
    isRoleBasedProject,
    ProjectContractorsService,
    projectContractorsMainQueryKey,
  } = useContractorsAssignmentsContext();

  const writeEntitySchema = useMemo(
    () =>
      isRoleBasedProject
        ? writeRoleBasedAssignments
        : writeDepartmentBasedAssignments,
    [isRoleBasedProject],
  );

  return useMutation<unknown, Error, WriteContractorAssignee>({
    mutationFn: async (contractorAssignment) => {
      await ProjectContractorsService.post([
        writeEntitySchema.parse(contractorAssignment),
      ]);
    },
    onSuccess: () => {
      toast({
        title: t("contractorsAssignments.success.create"),
      });
      queryClient.invalidateQueries({
        queryKey: [projectContractorsMainQueryKey],
      });
      onCloseDialog();
    },
    onError: (error) => {
      toast({
        title: t("contractorsAssignments.errors.create"),
        description: error.message,
        variant: "destructive",
      });
    },
  });
};
